import gsap from 'gsap';

const TRANSITION_DURATION = 0.5;
const EDGE_EPSILON_PX = 2;
const TOUCH_NOISE_THRESHOLD_PX = 10;
const DEFAULT_EXIT_THRESHOLD_PX = 100;
const SCROLL_IDLE_MS = 150;

type Direction = 'next' | 'prev';
type PanelState = 'active' | 'hidden' | 'overlay';

const PANEL_STATE_ATTR = 'data-panel-state';
const SELECTOR_STACK = '.js-scroll-stack';
const SELECTOR_PANEL = '.js-scroll-stack-panel';

const supportsScrollEnd = typeof HTMLElement !== 'undefined' && 'onscrollend' in HTMLElement.prototype;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isAtTopEdge(panel: HTMLElement): boolean {
  return panel.scrollTop <= EDGE_EPSILON_PX;
}

function isAtBottomEdge(panel: HTMLElement): boolean {
  return panel.scrollTop + panel.clientHeight >= panel.scrollHeight - EDGE_EPSILON_PX;
}

function panelIsScrollable(panel: HTMLElement): boolean {
  return panel.scrollHeight > panel.clientHeight + EDGE_EPSILON_PX;
}

function setPanelState(panel: HTMLElement, state: PanelState): void {
  if (state === 'active')
    panel.removeAttribute(PANEL_STATE_ATTR);
  else
    panel.setAttribute(PANEL_STATE_ATTR, state);
}

function setOverlayZ(panel: HTMLElement, zIndex: number): void {
  panel.style.setProperty('--scroll-stack-z', String(zIndex));
}

function clearPanelMotion(panel: HTMLElement): void {
  panel.style.removeProperty('--scroll-stack-z');
  gsap.set(panel, { clearProps: 'transform' });
}

function updateBeyondFirstFlag(stack: HTMLElement | null, activeIndex: number): void {
  if (!stack)
    return;

  if (activeIndex > 0)
    stack.setAttribute('data-beyond-first', '');
  else
    stack.removeAttribute('data-beyond-first');
}

function slidePanelIn(
  panel: HTMLElement,
  fromY: number,
  zIndex: number,
  duration: number,
  onComplete: () => void,
): void {
  setPanelState(panel, 'overlay');
  setOverlayZ(panel, zIndex);
  gsap.set(panel, { yPercent: fromY });
  gsap.to(panel, {
    yPercent: 0,
    duration,
    ease: 'power2.out',
    onComplete: () => {
      clearPanelMotion(panel);
      onComplete();
    },
  });
}

function setupReducedMotion(panels: HTMLElement[]): void {
  panels.forEach((panel, index) => {
    clearPanelMotion(panel);
    setPanelState(panel, index === 0 ? 'active' : 'hidden');
  });
}

function wheelDirection(deltaY: number): Direction | null {
  if (deltaY > 0)
    return 'next';
  if (deltaY < 0)
    return 'prev';
  return null;
}

function isTouchInsidePanel(panel: HTMLElement, event: TouchEvent): boolean {
  const touch = event.touches[0] ?? event.changedTouches[0];
  if (!touch)
    return false;

  const target = document.elementFromPoint(touch.clientX, touch.clientY);
  return target !== null && panel.contains(target);
}

function isAtEdgeForDirection(panel: HTMLElement, direction: Direction): boolean {
  return direction === 'next' ? isAtBottomEdge(panel) : isAtTopEdge(panel);
}

export function initScrollStack(): void {
  if (typeof window === 'undefined')
    return;

  const stack = document.querySelector<HTMLElement>(SELECTOR_STACK);
  if (!stack)
    return;

  const panels = gsap.utils.toArray<HTMLElement>(SELECTOR_PANEL, stack);
  if (panels.length === 0)
    return;

  const isMobileViewport = window.matchMedia('(width <= 800px)').matches;

  if (prefersReducedMotion() || isMobileViewport) {
    setupReducedMotion(panels);
    updateBeyondFirstFlag(stack, 0);
    return;
  }

  let activeIndex = 0;
  let isAnimating = false;
  let cooldownTimer: ReturnType<typeof setTimeout> | undefined;
  let touchPanel: HTMLElement | null = null;
  let wheelPanel: HTMLElement | null = null;
  let scrollPanel: HTMLElement | null = null;
  let scrollIdleTimer: ReturnType<typeof setTimeout> | undefined;
  let lastTouchY: number | null = null;
  let panelScrolling = false;
  let edgeIntentPx = 0;
  let edgeIntentDirection: Direction | null = null;

  const resetEdgeIntent = (): void => {
    edgeIntentPx = 0;
    edgeIntentDirection = null;
  };

  const clearScrollIdleTimer = (): void => {
    if (scrollIdleTimer === undefined)
      return;

    clearTimeout(scrollIdleTimer);
    scrollIdleTimer = undefined;
  };

  const setPanelScrollingIdle = (): void => {
    panelScrolling = false;
    clearScrollIdleTimer();
  };

  const onPanelScroll = (): void => {
    const panel = panels[activeIndex];
    panelScrolling = true;
    clearScrollIdleTimer();
    scrollIdleTimer = setTimeout(setPanelScrollingIdle, SCROLL_IDLE_MS);

    if (edgeIntentDirection === 'next' && !isAtBottomEdge(panel))
      resetEdgeIntent();
    else if (edgeIntentDirection === 'prev' && !isAtTopEdge(panel))
      resetEdgeIntent();
  };

  const onPanelScrollEnd = (): void => {
    setPanelScrollingIdle();
  };

  const bindPanelScroll = (panel: HTMLElement): void => {
    unbindPanelScroll();
    scrollPanel = panel;
    panel.addEventListener('scroll', onPanelScroll, { passive: true });
    if (supportsScrollEnd)
      panel.addEventListener('scrollend', onPanelScrollEnd, { passive: true });
  };

  const unbindPanelScroll = (): void => {
    if (scrollPanel === null)
      return;

    scrollPanel.removeEventListener('scroll', onPanelScroll);
    if (supportsScrollEnd)
      scrollPanel.removeEventListener('scrollend', onPanelScrollEnd);
    scrollPanel = null;
    setPanelScrollingIdle();
  };

  const accumulateEdgeIntent = (direction: Direction, deltaPx: number): boolean => {
    const activePanel = panels[activeIndex];

    if (!isAtEdgeForDirection(activePanel, direction)) {
      resetEdgeIntent();
      return false;
    }

    if (edgeIntentDirection !== direction) {
      edgeIntentPx = 0;
      edgeIntentDirection = direction;
    }

    edgeIntentPx += deltaPx;
    return edgeIntentPx >= DEFAULT_EXIT_THRESHOLD_PX;
  };

  const onTouchStart = (event: TouchEvent): void => {
    lastTouchY = event.touches[0]?.clientY ?? null;
  };

  const onTouchMove = (event: TouchEvent): void => {
    if (lastTouchY === null || touchPanel === null)
      return;

    if (!isTouchInsidePanel(touchPanel, event)) {
      lastTouchY = event.touches[0]?.clientY ?? null;
      return;
    }

    const currentY = event.touches[0]?.clientY ?? null;
    if (currentY === null)
      return;

    const deltaY = lastTouchY - currentY;
    lastTouchY = currentY;

    if (Math.abs(deltaY) < TOUCH_NOISE_THRESHOLD_PX)
      return;

    handleInterceptedGesture(deltaY > 0 ? 'next' : 'prev', Math.abs(deltaY), event);
  };

  const onTouchEnd = (): void => {
    lastTouchY = null;
  };

  const bindTouch = (panel: HTMLElement): void => {
    unbindTouch();
    touchPanel = panel;
    panel.addEventListener('touchstart', onTouchStart, { passive: true });
    panel.addEventListener('touchmove', onTouchMove, { passive: false });
    panel.addEventListener('touchend', onTouchEnd, { passive: true });
    panel.addEventListener('touchcancel', onTouchEnd, { passive: true });
  };

  const unbindTouch = (): void => {
    if (touchPanel === null)
      return;

    touchPanel.removeEventListener('touchstart', onTouchStart);
    touchPanel.removeEventListener('touchmove', onTouchMove);
    touchPanel.removeEventListener('touchend', onTouchEnd);
    touchPanel.removeEventListener('touchcancel', onTouchEnd);
    touchPanel = null;
    lastTouchY = null;
  };

  const onWheel = (event: WheelEvent): void => {
    const direction = wheelDirection(event.deltaY);
    if (direction === null)
      return;

    handleInterceptedGesture(direction, Math.abs(event.deltaY), event);
  };

  const bindWheel = (panel: HTMLElement): void => {
    unbindWheel();
    wheelPanel = panel;
    panel.addEventListener('wheel', onWheel, { passive: false });
  };

  const unbindWheel = (): void => {
    if (wheelPanel === null)
      return;

    wheelPanel.removeEventListener('wheel', onWheel);
    wheelPanel = null;
  };

  const bindActivePanel = (panel: HTMLElement): void => {
    bindTouch(panel);
    bindWheel(panel);
    bindPanelScroll(panel);
    resetEdgeIntent();
    panelScrolling = false;
  };

  const unbindActivePanel = (): void => {
    unbindTouch();
    unbindWheel();
    unbindPanelScroll();
    resetEdgeIntent();
  };

  panels.forEach((panel, index) => {
    clearPanelMotion(panel);
    setPanelState(panel, index === 0 ? 'active' : 'hidden');
  });

  updateBeyondFirstFlag(stack, 0);
  bindActivePanel(panels[0]);

  const scheduleCooldown = (): void => {
    cooldownTimer = setTimeout(() => {
      cooldownTimer = undefined;
    }, TRANSITION_DURATION * 1000);
  };

  const canTransition = (direction: Direction): boolean => {
    const activePanel = panels[activeIndex];
    if (direction === 'next') {
      if (activeIndex >= panels.length - 1)
        return false;
      if (!panelIsScrollable(activePanel))
        return true;
      return isAtBottomEdge(activePanel);
    }

    if (activeIndex <= 0)
      return false;
    if (!panelIsScrollable(activePanel))
      return true;
    return isAtTopEdge(activePanel);
  };

  const transitionTo = (nextIndex: number, direction: Direction): void => {
    if (isAnimating)
      return;

    const current = panels[activeIndex];
    const next = panels[nextIndex];
    const fromY = direction === 'next' ? 100 : -100;

    isAnimating = true;
    unbindActivePanel();

    slidePanelIn(next, fromY, nextIndex + 2, TRANSITION_DURATION, () => {
      current.scrollTop = 0;
      clearPanelMotion(current);
      setPanelState(current, 'hidden');

      next.scrollTop = 0;
      setPanelState(next, 'active');

      activeIndex = nextIndex;
      isAnimating = false;
      updateBeyondFirstFlag(stack, activeIndex);
      bindActivePanel(next);
    });
  };

  const requestTransition = (direction: Direction): boolean => {
    if (isAnimating)
      return false;

    if (!canTransition(direction))
      return false;

    transitionTo(direction === 'next' ? activeIndex + 1 : activeIndex - 1, direction);
    return true;
  };

  const handleInterceptedGesture = (direction: Direction, deltaPx: number, event: Event): void => {
    if (cooldownTimer !== undefined)
      return;

    const activePanel = panels[activeIndex];
    const scrollable = panelIsScrollable(activePanel);

    if (!canTransition(direction))
      return;

    if (scrollable && panelScrolling)
      return;

    if (!scrollable) {
      if (!requestTransition(direction))
        return;

      resetEdgeIntent();
      event.preventDefault();
      scheduleCooldown();
      return;
    }

    if (!accumulateEdgeIntent(direction, deltaPx)) {
      event.preventDefault();
      return;
    }

    resetEdgeIntent();

    if (!requestTransition(direction))
      return;

    event.preventDefault();
    scheduleCooldown();
  };
}
